import { useState, useEffect, useCallback } from 'react';
import { API_BASE_URL } from '../utils/apiConfig';

/**
 * Custom hook to fetch top coins ranked by whale trade count (24h)
 * @param {number} limit - Number of top coins to fetch (default: 6)
 * @returns {Object} { coins, loading, error, refresh }
 */
export function useTopCoinsByCount(limit = 6, { enabled = true } = {}) {
  const [coins, setCoins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTopCoins = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/top-coins-by-count?limit=${limit}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch top coins: ${response.status}`);
      }

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch top coins');
      }

      setCoins(result.data);
      setError(null);
    } catch (err) {
      console.error('[TopCoins] Fetch error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [limit]);

  useEffect(() => {
    if (!enabled) return;

    fetchTopCoins();

    // Refresh every 60 seconds
    const interval = setInterval(fetchTopCoins, 60000);

    return () => clearInterval(interval);
  }, [fetchTopCoins, enabled]);

  return {
    coins,
    loading,
    error,
    refresh: fetchTopCoins
  };
}

export default useTopCoinsByCount;
